// src/services/consoleApi.js
// Client for the platform-admin operations console (/api/platform/console).
import { authFetch } from "./http";

async function readJson(res, fallback) {
  const text = await res.text();
  let data = {};
  try {
    data = text ? JSON.parse(text) : {};
  } catch {
    /* leave data = {} */
  }
  if (!res.ok) {
    throw new Error(data?.message || data?.error || `${fallback} (${res.status})`);
  }
  return data;
}

export async function getConsoleSession() {
  const res = await authFetch("/platform/console/session");
  return readJson(res, "Console access check failed");
}

export async function getConsoleCommands() {
  const res = await authFetch("/platform/console/commands");
  return readJson(res, "Failed to load commands");
}

export async function getConsoleHistory() {
  const res = await authFetch("/platform/console/history");
  return readJson(res, "Failed to load run history");
}

/**
 * Run one allowlisted command. Resolves with the run result even when the
 * command itself failed (ok:false + output), and rejects only on transport or
 * validation errors.
 */
export async function runConsoleCommand({ command, args, confirm }) {
  const res = await authFetch("/platform/console/run", {
    method: "POST",
    body: JSON.stringify({ command, args, confirm: !!confirm }),
  });
  const text = await res.text();
  let data = {};
  try {
    data = text ? JSON.parse(text) : {};
  } catch {
    /* leave data = {} */
  }
  // A 4xx means the request was rejected before the command ran. A command that
  // ran and failed still comes back 200 with ok:false, and is shown as output.
  if (!res.ok) {
    throw new Error(data?.message || data?.error || `Run failed (${res.status})`);
  }
  return data;
}

export async function getConsoleArtifacts() {
  const res = await authFetch("/platform/console/artifacts");
  return readJson(res, "Failed to list artifacts");
}

/** Download an artifact through authFetch (the endpoint requires a bearer token). */
export async function downloadArtifact(name) {
  const res = await authFetch(
    `/platform/console/artifacts/${name.split("/").map(encodeURIComponent).join("/")}`
  );
  if (!res.ok) throw new Error(`Download failed (${res.status})`);

  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = name.split("/").pop();
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}
